import * as Sentry from '@sentry/react';
import { lazy, useSyncExternalStore } from 'react';
import { createRootRoute, Outlet } from '@tanstack/react-router';
import { OfflineBanner, SessionExpiredOverlay, Toaster, useOnlineRecoveryToast } from '@/shared/ui';
import { sessionExpiredStore } from '@/shared/lib/session-expired-store';
import { Providers } from '../providers';

const TanStackRouterDevtools = import.meta.env.DEV
  ? lazy(() =>
      import('@tanstack/react-router-devtools').then((m) => ({
        default: m.TanStackRouterDevtools,
      })),
    )
  : () => null;

function ErrorFallback() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <h1 className="text-2xl font-semibold">문제가 발생했습니다</h1>
        <p className="mt-2 text-muted-foreground">잠시 후 다시 시도해주세요.</p>
        <button
          type="button"
          className="mt-4 rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
          onClick={() => window.location.reload()}
        >
          새로고침
        </button>
      </div>
    </div>
  );
}

function NotFoundPage() {
  return (
    <div className="flex min-h-screen items-center justify-center">
      <div className="text-center">
        <p className="text-6xl font-bold text-muted-foreground/30">404</p>
        <h1 className="mt-4 text-2xl font-semibold">페이지를 찾을 수 없습니다</h1>
        <p className="mt-2 text-muted-foreground">
          요청하신 페이지가 존재하지 않거나 이동되었습니다.
        </p>
        <a
          href="/"
          className="mt-6 inline-block rounded-md bg-primary px-4 py-2 text-sm text-primary-foreground"
        >
          홈으로 돌아가기
        </a>
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});

function RootContent() {
  useOnlineRecoveryToast();
  const isSessionExpired = useSyncExternalStore(
    sessionExpiredStore.subscribe,
    sessionExpiredStore.getSnapshot,
  );

  return (
    <>
      <OfflineBanner />
      <Outlet />
      <Toaster />
      {isSessionExpired && <SessionExpiredOverlay />}
      <TanStackRouterDevtools position="bottom-right" />
    </>
  );
}

function RootComponent() {
  return (
    <Sentry.ErrorBoundary fallback={ErrorFallback}>
      <Providers>
        <RootContent />
      </Providers>
    </Sentry.ErrorBoundary>
  );
}
